import { Injectable } from '@angular/core'
import {
  BasePageMetaResolver,
  BreadcrumbMeta,
  CmsService,
  OccEndpointsService,
  PageLinkService,
  ProductPageMetaResolver,
  RoutingService,
  SiteContextParamsService,
  TranslationService,
  WindowRef
} from '@spartacus/core'
import { combineLatest, Observable, of } from 'rxjs'
import { map, switchMap } from 'rxjs/operators'
import { StringUtil } from '../../../../util/string-util'
import {
  EbAlternateLink,
  EBOGPMetaData,
  EbPage,
  EBStructuredProductData,
  PageAlternatesResolver
} from '../../../cms/interfaces/eb-page.interface'
import { EbBaseSiteService } from '../../../site-context/services/eb-base-site.service'
import { EbProduct, EbProductTypeEnum } from '../../interfaces/eb-product.interface'
import { EbProductService } from './eb-product.service'

@Injectable({
  providedIn: 'root'
})
export class EbProductPageMetaResolver
  extends ProductPageMetaResolver
  implements PageAlternatesResolver
{
  constructor(
    protected routingService: RoutingService,
    protected productService: EbProductService,
    protected translation: TranslationService,
    protected basePageMetaResolver: BasePageMetaResolver,
    protected pageLinkService: PageLinkService,
    protected cms: CmsService,
    protected baseSiteService: EbBaseSiteService,
    protected siteContextParamsService: SiteContextParamsService,
    protected occEndpoints: OccEndpointsService,
    protected winRef: WindowRef
  ) {
    super(routingService, productService, translation, basePageMetaResolver, pageLinkService)
  }
  protected product$: Observable<EbProduct>
  protected page$ = this.cms.getCurrentPage() as Observable<EbPage>

  resolveTitle(): Observable<string> {
    return combineLatest([this.product$, this.page$]).pipe(
      switchMap(([currentProduct, page]) => {
        const metaTitle = page?.metaTitle ?? ''
        const breadcrumbsReverted = [...(currentProduct?.breadcrumbs ?? [])].reverse()
        const title = []
        let charCount = currentProduct?.name?.length + metaTitle?.length
        title.push(currentProduct?.name)
        breadcrumbsReverted.forEach(breadcrumb => {
          if (charCount < 70) {
            title.push(breadcrumb?.facetName)
            charCount += breadcrumb?.facetName.length
          }
        })
        if (metaTitle) {
          title.push(metaTitle)
        }

        return this.translation.translate('pageMetaResolver.product.title', {
          title: title.join(' | ')
        })
      })
    )
  }

  resolveHeading(): Observable<string> {
    return this.product$.pipe(
      switchMap((currentProduct: EbProduct) =>
        this.translation.translate('pageMetaResolver.product.heading', {
          heading:
            currentProduct?.ebProductType === EbProductTypeEnum.VARIANT
              ? currentProduct.parentProductName
              : currentProduct?.name
        })
      )
    )
  }

  resolveDescription(): Observable<string> {
    return combineLatest([this.product$, this.page$]).pipe(
      switchMap(([currentProduct, page]) => {
        const description = []
        const breadcrumbsReverted = [...(currentProduct?.breadcrumbs ?? [])].reverse()
        description.push(
          currentProduct?.ebProductType === EbProductTypeEnum.VARIANT
            ? currentProduct.parentProductName
            : currentProduct?.name
        )
        if (breadcrumbsReverted.length) {
          description.push(...breadcrumbsReverted.map(breadcrumb => breadcrumb.facetName))
        }
        if (currentProduct?.description) {
          description.push(StringUtil.truncateWord(currentProduct.description, 200, false, 50))
        }
        if (page?.metaDescription) {
          description.push(page?.metaDescription)
        }

        return this.translation.translate('pageMetaResolver.product.description', {
          description: description.join(' | ')
        })
      })
    )
  }

  resolveBreadcrumbs(): Observable<BreadcrumbMeta[]> {
    return combineLatest([this.product$, this.translation.translate('common.home')]).pipe(
      map(([currentProduct, label]) => {
        const breadcrumbs = []
        breadcrumbs.push({ label, link: '/' })
        for (const { facetCode, facetName } of currentProduct?.breadcrumbs || []) {
          breadcrumbs.push({
            label: facetName,
            link: '/c/' + facetCode
          } as BreadcrumbMeta)
        }
        return breadcrumbs
      })
    )
  }

  resolveAlternates(): Observable<EbAlternateLink[]> {
    return combineLatest([this.baseSiteService.getAll(), this.product$]).pipe(
      map(([allBaseSites, currentProduct]) => {
        const alternates = []
        if (this.isVariant(currentProduct)) {
          allBaseSites.forEach(baseSite => {
            baseSite.languages.forEach(language => {
              let url = this.routingService.getFullUrl({
                cxRoute: 'product',
                params: currentProduct
              })
              url = url.replace('/p/', '/v/')
              const countryFromUrl = this.siteContextParamsService.getValue('country')
              const languageFromUrl = this.siteContextParamsService.getValue('language')
              const urlWithCountryLanguagePattern = url.replace(
                `${countryFromUrl}/${languageFromUrl}`,
                `${baseSite.country.isocode}/${language.isocode}`
              )
              const hrefLang = `${language.isocode}-${baseSite.country.isocode.toLowerCase()}`
              alternates.push({
                url: decodeURIComponent(urlWithCountryLanguagePattern),
                hrefLang
              })
            })
          })
          return alternates
        }
        return []
      })
    )
  }

  resolveOGPMetaData(): Observable<EBOGPMetaData> {
    return this.product$.pipe(
      switchMap(currentProduct =>
        this.productService.getProductMetaDataForSEO(currentProduct.code)
      ),
      switchMap(productMetaData => {
        const baseUrlWithoutPrefixAndBaseSite = this.occEndpoints.getBaseUrl({
          baseUrl: true,
          prefix: false,
          baseSite: false
        })
        const imageUrl = productMetaData.image?.includes(baseUrlWithoutPrefixAndBaseSite)
          ? productMetaData.image
          : baseUrlWithoutPrefixAndBaseSite + productMetaData.image
        const tempUrl = productMetaData.url?.includes(this.winRef.location.origin)
          ? productMetaData.url
          : `${this.winRef.location.origin}/${productMetaData.url}`
        return of({
          title: productMetaData.name,
          description: productMetaData.description,
          type: 'website',
          url: tempUrl,
          image: imageUrl
        })
      })
    )
  }

  resolveStructuredProductData(): Observable<EBStructuredProductData> {
    return this.product$.pipe(
      switchMap(currentProduct =>
        this.productService.getProductMetaDataForSEO(currentProduct.code)
      ),
      map(structuredData => {
        const baseUrlWithoutPrefixAndBaseSite = this.occEndpoints.getBaseUrl({
          baseUrl: true,
          prefix: false,
          baseSite: false
        })
        // adds the needed '@' sign to all type and context attributes
        const normalizedStructuredData = this.getNormalizedData(structuredData)
        if (structuredData.image) {
          const imageUrl = structuredData.image?.includes(baseUrlWithoutPrefixAndBaseSite)
            ? structuredData.image
            : baseUrlWithoutPrefixAndBaseSite + structuredData.image
          structuredData.image = imageUrl
        }
        if (structuredData.logo) {
          const logoUrl = structuredData.logo?.includes(baseUrlWithoutPrefixAndBaseSite)
            ? structuredData.logo
            : baseUrlWithoutPrefixAndBaseSite + structuredData.logo
          structuredData.logo = logoUrl
        }
        if (structuredData.isVariantOf && structuredData.isVariantOf.image) {
          const imageUrl = structuredData.isVariantOf.image.includes(
            baseUrlWithoutPrefixAndBaseSite
          )
            ? structuredData.isVariantOf.image
            : baseUrlWithoutPrefixAndBaseSite + structuredData.isVariantOf.image
          structuredData.isVariantOf.image = imageUrl
        }
        if (structuredData.url) {
          const tempUrl = structuredData.url?.includes(this.winRef.location.origin)
            ? structuredData.url
            : `${this.winRef.location.origin}/${structuredData.url}`
          structuredData.url = tempUrl
        }
        return structuredData
      })
    )
  }

  resolveCanonicalUrl(): Observable<string> {
    return this.product$.pipe(
      switchMap(currentProduct => this.findBaseProduct(currentProduct)),
      map((currentProduct: EbProduct) => {
        let url = this.routingService.getFullUrl({
          cxRoute: 'product',
          params: currentProduct
        })
        if (this.isVariant(currentProduct)) {
          url = url.replace('/p/', '/v/')
        }
        return this.pageLinkService.getCanonicalUrl({}, decodeURIComponent(url))
      })
    )
  }

  private getNormalizedData(obj) {
    for (const key in obj) {
      if (key) {
        if (typeof obj[key] === 'object') {
          this.getNormalizedData(obj[key])
        } else {
          if (key === 'type' || key === 'context') {
            delete Object.assign(obj, { [`@${key}`]: obj[key] })[key]
          }
        }
      }
    }
    return obj
  }

  isVariant(product): boolean {
    return product?.ebProductType === EbProductTypeEnum.VARIANT
  }
}
